import React, { forwardRef } from "react";
import { RefreshIcon } from "@heroicons/react/outline";
import { cn } from "./cn";
import "./Button.css";

/**
 * Bun button. Spec: Component Library "Buttons".
 *
 * variant: "primary" (ink — the default action), "secondary" (outline), "surface" (filled neutral),
 *          "accent" (blue — sparingly, one per view), "accent-soft" (blue tint), "ghost" (borderless),
 *          "danger" (destructive outline), "warning", "success" (confirmed state).
 * size:    "md" (13px / radius 10) | "sm" (12px / radius 7, table rows).
 * iconOnly: circular button for a lone icon; shape="rounded" keeps rounded-box corners.
 * Small text buttons use a 32px mobile minimum; other buttons retain 44px.
 * Callers own outer placement only; appearance and internal spacing stay here.
 */
const VARIANTS = {
  primary: "bg-bun-ink text-bun-on-ink hover:bg-bun-ink-hover",
  secondary:
    "bg-transparent text-bun-slate border border-bun-line hover:bg-bun-mist",
  surface:
    "bg-bun-surface text-bun-slate border border-bun-line hover:bg-bun-mist",
  accent: "bg-bun-blue text-white hover:bg-bun-blue-hover",
  'accent-soft': "bg-bun-tint-blue text-bun-blue border border-bun-tint-blue-line hover:bg-bun-wash",
  ghost: "bg-transparent text-bun-stone hover:bg-bun-mist-deep hover:text-bun-text",
  danger:
    "bg-transparent text-bun-danger border border-bun-danger-border hover:bg-bun-danger-bg",
  warning:
    "bg-bun-warning-bg text-bun-warning border border-bun-warning-border hover:bg-bun-warning-strong-bg",
  success:
    "bg-bun-success-bg text-bun-success border border-bun-success-border hover:border-bun-success",
};

const SIZES = {
  md: "px-4 py-[9px] text-bun-body rounded-bun gap-[7px]",
  sm: "px-3 py-[5px] text-bun-meta rounded-bun-sm gap-[6px]",
};

const ICON_ONLY_SIZES = {
  md: "w-[34px] h-[34px] p-0 rounded-bun",
  sm: "w-[26px] h-[26px] p-0 rounded-bun-sm",
};

const Button = forwardRef(function Button(
  {
    as: Component = "button",
    type,
    variant = "primary",
    size = "md",
    shape,
    selected,
    iconOnly = false,
    loading = false,
    loadingLabel,
    revealOnHover = false,
    className,
    children,
    disabled,
    "aria-label": ariaLabel,
    "aria-busy": ariaBusy,
    "aria-pressed": ariaPressed,
    ...props
  },
  ref
) {
  return (
    <Component
      {...props}
      ref={ref}
      type={Component === "button" ? type || "button" : undefined}
      disabled={Component === "a" ? undefined : disabled || loading}
      aria-busy={loading ? true : ariaBusy}
      aria-pressed={ariaPressed ?? selected}
      aria-label={loading && loadingLabel ? loadingLabel : ariaLabel}
      className={cn(
        "inline-flex items-center justify-center sm:min-h-0 whitespace-nowrap font-instrument font-strong cursor-pointer",
        size === "sm" && !iconOnly ? "min-h-8" : "min-h-11",
        "focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bun-blue",
        "disabled:bg-bun-disabled-bg disabled:text-bun-disabled-text disabled:border-transparent disabled:cursor-not-allowed",
        VARIANTS[variant],
        SIZES[size],
        selected && "bg-bun-mist-deep text-bun-text hover:bg-bun-mist-deep hover:text-bun-text",
        iconOnly && "min-w-11 sm:min-w-0",
        iconOnly && ICON_ONLY_SIZES[size],
        (shape === "round" || (iconOnly && shape !== "rounded")) && "rounded-full",
        revealOnHover && "bun-hover-action",
        className
      )}
    >
      {loading && <RefreshIcon className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />}
      {!(loading && iconOnly) && children}
    </Component>
  );
});

export default Button;
